import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Builder } from 'builder-pattern';
import {
  formatFilterDate,
  hasActiveDateRange,
  isEmptyFilterValue,
  toDatepickerValue,
} from 'src/app/pages/operation/shared/operation-filter.utils';

import { OperationFilter } from '../../shared/operation-filter';
import { OperationConfirmationService } from '../shared/operation-confirmation.service';

@Component({
  standalone: false,
  selector: 'app-operation-confirmation-filter',
  templateUrl: './operation-confirmation-filter.component.html',
  styleUrls: ['./operation-confirmation-filter.component.scss'],
})
export class OperationConfirmationFilterComponent implements OnInit, OnChanges {
  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() closed = new EventEmitter<void>();

  @Input() get isOpen(): boolean {
    return this._isOpen;
  }

  set isOpen(value: boolean) {
    this._isOpen = value;
    this.isOpenChange.emit(this._isOpen);
  }

  private _isOpen = false;

  formFilter: FormGroup = new FormGroup({});
  datepickerResetKey = 0;
  periodStartDate: Date | null = null;
  periodEndDate: Date | null = null;
  invalidPeriodDate = false;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly confirmationService: OperationConfirmationService,
  ) {}

  ngOnInit(): void {
    this.setForm();
    this.loadFormFromFilters();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue === true) {
      this.loadFormFromFilters();
    }
  }

  get minFinalDateIso(): string {
    return this.periodStartDate ? this.toIsoDate(this.periodStartDate) : '';
  }

  get maxInitialDateIso(): string {
    return this.periodEndDate ? this.toIsoDate(this.periodEndDate) : '';
  }

  setForm(): void {
    this.formFilter = this.formBuilder.group({
      holderAccount: [''],
      carbonCreditCode: [''],
      initialDate: [null],
      finalDate: [null],
    });

    this.formFilter.get('initialDate')?.valueChanges.subscribe(value => {
      this.periodStartDate = isEmptyFilterValue(value) ? null : toDatepickerValue(value);
      this.revalidatePeriodDates();
    });

    this.formFilter.get('finalDate')?.valueChanges.subscribe(value => {
      this.periodEndDate = isEmptyFilterValue(value) ? null : toDatepickerValue(value);
      this.revalidatePeriodDates();
    });
  }

  loadFormFromFilters(): void {
    const filters = this.confirmationService.filters.getValue();
    const hasDateFilter = hasActiveDateRange(filters);

    this.formFilter.setValue({
      holderAccount: filters.holderCode ?? '',
      carbonCreditCode: filters.carbonCreditCode ?? '',
      initialDate: hasDateFilter ? toDatepickerValue(filters.initialDate) : null,
      finalDate: hasDateFilter ? toDatepickerValue(filters.finalDate) : null,
    });

    this.syncPeriodDatesFromForm();
  }

  cleanForm(): void {
    this.formFilter.reset({
      holderAccount: '',
      carbonCreditCode: '',
      initialDate: null,
      finalDate: null,
    });
    this.formFilter.get('initialDate')?.setValue(null);
    this.formFilter.get('finalDate')?.setValue(null);
    this.resetPeriodDateValidation();
    this.refreshDatepickerViews();
    this.confirmationService.filters.next({} as OperationFilter);
  }

  filter(): void {
    this.syncPeriodDatesFromForm();

    if (this.invalidPeriodDate) {
      return;
    }

    this.confirmationService.filters.next(this.buildFiltersFromForm());
    this.close();
  }

  close(): void {
    this.closed.emit();
  }

  onInitialDateChange(event: Event): void {
    this.onDateChange(event, 'initialDate');
  }

  onFinalDateChange(event: Event): void {
    this.onDateChange(event, 'finalDate');
  }

  trackDatepickerResetKey(_index: number, key: number): number {
    return key;
  }

  private onDateChange(event: Event, controlName: 'initialDate' | 'finalDate'): void {
    const iso = (event as CustomEvent<{ value?: string }>).detail?.value?.trim() ?? '';

    if (!iso) {
      this.formFilter.get(controlName)?.setValue(null);
      return;
    }

    const date = toDatepickerValue(iso);

    if (!date) {
      return;
    }

    this.formFilter.get(controlName)?.setValue(date);
  }

  private syncPeriodDatesFromForm(): void {
    const initialDate = this.formFilter.get('initialDate')?.value;
    const finalDate = this.formFilter.get('finalDate')?.value;

    this.periodStartDate = isEmptyFilterValue(initialDate) ? null : toDatepickerValue(initialDate);
    this.periodEndDate = isEmptyFilterValue(finalDate) ? null : toDatepickerValue(finalDate);
    this.revalidatePeriodDates();
  }

  private revalidatePeriodDates(): void {
    this.invalidPeriodDate = !!this.periodStartDate
      && !!this.periodEndDate
      && this.periodStartDate > this.periodEndDate;
  }

  private resetPeriodDateValidation(): void {
    this.periodStartDate = null;
    this.periodEndDate = null;
    this.invalidPeriodDate = false;
  }

  private toIsoDate(date: Date): string {
    return formatFilterDate(date) ?? '';
  }

  private refreshDatepickerViews(): void {
    this.datepickerResetKey++;
  }

  private buildFiltersFromForm(): OperationFilter {
    const formValues = this.formFilter.getRawValue();
    const initialDate = formatFilterDate(formValues.initialDate);
    const finalDate = formatFilterDate(formValues.finalDate);
    const dateRange = hasActiveDateRange({ initialDate, finalDate });

    return Builder<OperationFilter>()
      .carbonCreditCode(formValues.carbonCreditCode?.trim() || undefined)
      .holderCode(formValues.holderAccount?.trim() || undefined)
      .initialDate(dateRange ? initialDate : undefined)
      .finalDate(dateRange ? finalDate : undefined)
      .build();
  }
}
